import React, {useContext} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../../../../stores";
import {Chip, Collapse, IconButton, TableCell, TableRow} from "@mui/material";
import {ExpandMore, KeyboardArrowDown, KeyboardArrowRight,} from "@mui/icons-material";
import {CurrencyComponent} from "../../../../../components/currency-component/CurrencyComponent";
import {CategoryMenuControllerContext} from "../../../context/CategoryMenuController";
import {GroupedCategorizationReviewSubTable} from "./GroupedCategorizationReviewSubTable";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "../../../../../constants";
import {GroupedRowState} from "../../../classes/GroupedRowState";
import {getCatReviewColumns} from "../columns";
import {MithraTableCell} from "../../../../../components/table/MithraTableCell";
import {getCollapseAnimationTiming} from "../../../../../components/table/utils";
import {GoogleLink} from "../../../../../components/_tiny/GoogleLink";
import {CombinedReviewButtons} from "../../approval-acc-rej/CombinedReviewButtons";
import {fromArrayCategories} from "../../../../../services/ApiHelpers";
import {ReviewChoice} from "../../../../../services/classes/MaterializedClasses";
import {environment} from "../../../../../env";
import {PartReCategorizationComponent} from "../../../../../components/categorization/PartReCategorizationComponent";
import {getCategorizationStateClassName} from "../../../classes/CategorizationUtils";
import {MultiCompanyName} from "../../../../../components/company-name/MultiCompanyName";
import {
    CategorizationViewOnlyComponent
} from "../../../../../components/categorization/CategorizationViewOnlyComponent";
import {TaxonomyCategory} from "../../../../../stores/TaxonomyCategoryProvider";
import {RootSelectionBox} from "../selection/RootSelectionBox";
import {ExtendedColSpec} from "../../../../../components/table/MithraTableHeadColumns";
import {GroupSelectionBox} from "../selection/GroupSelectionBox";

type Props = {
    groupedRow: GroupedRowState
};
export const GroupedCategorizationReviewMainRow: React.FC<Props> = observer(({groupedRow}) => {
    const {p, categorizationReviewStore, bagStore} = useStores();
    const controller = useContext(CategoryMenuControllerContext);
    const reviewPageController = categorizationReviewStore.reviewPageController;
    const reviewPageSelectionController = categorizationReviewStore.reviewPageController.reviewPageSelectionController;
    const selectionController = categorizationReviewStore.reviewPageController.reviewPageSelectionController;
    const filterController = categorizationReviewStore.reviewPageController.reviewPageFilterController;
    const open = groupedRow.open;
    const firstElement = categorizationReviewStore.showReviewSelectionBoxes ? {
        cls: 'col-selection',
        txt: <RootSelectionBox/>,
        align: 'center'
    } as ExtendedColSpec : null;
    const COLUMNS = getCatReviewColumns(p, firstElement);
    const hasPartData = groupedRow.partStates !== undefined && groupedRow.partStates.length > 0;
    const canEdit: boolean =
        // The view must be in edit mode
        categorizationReviewStore.canEditReviewPage
        // We need to be in a state that allows editing
        && groupedRow.combinedStateVisual?.reviewEditAllowed
        // // We should have a taxonomy loaded
        // && (categorizationReviewStore.categories.taxonomy_categories !== null || categorizationReviewStore.categories.taxonomy_categories !== undefined)
        // It should not be inconsistent
        && !groupedRow.combinedStateLoading;

    let onAccept: undefined | (() => void) = undefined;
    let newCatsFunc: undefined | ((c: TaxonomyCategory) => void) = undefined;
    if (canEdit) {
        onAccept = () => reviewPageController.onClickAcceptRejectSupplierParts(groupedRow, ReviewChoice.ACCEPT)
    }
    newCatsFunc = (c: TaxonomyCategory) => reviewPageController.onClickReCategorizeSupplierParts(groupedRow, c.data.labels)


    let recatControlsComponent: React.ReactNode;
    if (groupedRow.combinedStateVisual) {
        let combinedCategory = groupedRow.combinedStateVisual.combinedCategory;
        if (combinedCategory === false || combinedCategory === 'uncategorized') {
            // There is no common category
            recatControlsComponent = <>
                <Chip
                    className={'grouped-categorization-review-main-row__suggested-categories filled'}
                    size="small"
                    label={combinedCategory === false ? 'Mixed categories' : 'Uncategorized'}
                    disabled={!canEdit}
                    icon={<KeyboardArrowDown/>}
                    onClick={(event) => {
                        if (!newCatsFunc) return;
                        if (!controller) {
                            console.error('No controller set');
                            return;
                        }
                        // When the category component is opened, reset the selection to avoid confusion
                        reviewPageSelectionController.onDeselectAll();

                        const anchor: HTMLElement = event.currentTarget;
                        controller.setOnSelect(newCatsFunc)
                        controller.open(anchor as HTMLButtonElement)
                    }}
                />
            </>
        } else {
            // There is a single common category
            const effectiveLabels = fromArrayCategories(combinedCategory, bagStore.taxonomy_size);

            if (!canEdit) {
                recatControlsComponent = <CategorizationViewOnlyComponent category={effectiveLabels}/>
            } else {
                recatControlsComponent = <PartReCategorizationComponent
                    cats={effectiveLabels}
                    handleOpenSearch={event => {
                        if (!newCatsFunc) return;
                        if (!controller) {
                            console.error('No controller set');
                            return;
                        }

                        // When the category component is opened, reset the selection to avoid confusion
                        reviewPageSelectionController.onDeselectAll();

                        const anchor = event.currentTarget;
                        controller.setOnSelect(newCatsFunc)
                        controller.open(anchor)
                        //Make the controller make that this has been recategorized
                    }}
                    selectedL1Category={filterController.selectedL1Category}
                />
            }
        }

        // if (groupedRow.data.total_all_approval_set) {
        //     recatControlsComponent = <>
        //         {recatControlsComponent}
        //         <Alert severity="warning">
        //             All parts are already sent for approval
        //         </Alert>
        //     </>
        // } else if (groupedRow.data.total_exists_approval_set) {
        //     recatControlsComponent = <>
        //         {recatControlsComponent}
        //         <Alert severity="warning">
        //             Some elements are already sent for approval
        //         </Alert>
        //     </>
        // }

    }
    let i = 0;
    return <>
        <TableRow
            className={`supplier-row ${groupedRow.hasSelection ? 'is-selected' : ''} ${groupedRow.combinedStateLoading ? 'loading' : ''} ${groupedRow.combinedStateVisual.selectionDisabled ? 'selection-disabled' : ''}`}>
            {categorizationReviewStore.showReviewSelectionBoxes && <MithraTableCell c={COLUMNS[i++]}>
                <GroupSelectionBox groupedRow={groupedRow}/>
            </MithraTableCell>}
            <MithraTableCell c={COLUMNS[i++]} onClick={() => groupedRow.setOpen(!groupedRow.open)}>
                {
                    <IconButton aria-label="expand row" size="small">
                        {open ? <ExpandMore/> : <KeyboardArrowRight/>}
                    </IconButton>
                }
                {groupedRow.partCount}
            </MithraTableCell>

            {/* Field not available in API */}
            {/*{p.p.showSupplierIdInCategorization && <MithraTableCell c={COLUMNS[i++]}>*/}
            {/*    {groupedRow.data.s_id}*/}
            {/*</MithraTableCell>}*/}

            <MithraTableCell c={COLUMNS[i++]}>
                <GoogleLink type="supplier" objs={[
                    groupedRow.data,
                    groupedRow.partStates ? groupedRow.partStates[0]?.data : undefined,
                ]}>
                    {groupedRow.data.s_name}
                </GoogleLink>

                {environment.isTestReviewPage && <>
                    <br/>
                    {/*combined_state_v2={JSON.stringify(groupedRow.combinedStateVisual)}<br/>*/}
                    all_is_reviewed=<b>{JSON.stringify(groupedRow.data.total_all_is_reviewed)}</b><br/>
                    {/*data={JSON.stringify(groupedRow.data)}<br/>*/}
                </>}

            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]}>
                <CurrencyComponent v={groupedRow.spend} nDigits={3}/>
            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]}>
                {groupedRow.data.total_p_l1?.map(l => l === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : l).join(', ')}
            </MithraTableCell>
            {!p.p.hideBuInCategorizationTable &&
                <MithraTableCell c={COLUMNS[i++]}>
                    <MultiCompanyName labels={groupedRow.data.total_bu_names?.map(l => l === null ? 'None' : l) ?? []}/>
                </MithraTableCell>
            }
            <MithraTableCell c={COLUMNS[i++]}>
                {recatControlsComponent}

                {environment.isTestReviewPage && <>
                    <br/>
                    supplierId=<b>{JSON.stringify(groupedRow.supplierId)}</b> |
                    mat_s_group_ids=<b>{JSON.stringify(groupedRow.data.mat_s_group_ids)}</b> |
                    loading=<b>{'' + groupedRow.combinedStateLoading}</b>
                    <br/>

                    exists_invalid_suggestion=<b>{JSON.stringify(groupedRow.data.total_exists_invalid_suggestion)}</b> |
                    <br/>

                    total_all_is_reviewed=<b>{JSON.stringify(groupedRow.data.total_all_is_reviewed)}</b><br/>
                    total_exists_approval_set=<b>{JSON.stringify(groupedRow.data.total_exists_approval_set)}</b><br/>
                    total_exists_approval_applied=<b>{JSON.stringify(groupedRow.data.total_exists_approval_applied)}</b><br/>
                    total_all_approval_set=<b>{JSON.stringify(groupedRow.data.total_all_approval_set)}</b><br/>
                    total_all_approval_applied=<b>{JSON.stringify(groupedRow.data.total_all_approval_applied)}</b><br/>
                </>}
            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]}>
                <Chip
                    label={(groupedRow.combinedStateVisual.approvalState)}
                    className={'chip ' + getCategorizationStateClassName(groupedRow.combinedStateVisual.approvalState)}/>
            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]}>
                <CombinedReviewButtons onAccept={onAccept} groupedRow={groupedRow}/>
            </MithraTableCell>
        </TableRow>
        <TableRow>
            {/*<p>*/}
            {/*    dataPage={'' + page}<br/>*/}
            {/*    dataCount={'' + groupedRow.partCount}<br/>*/}
            {/*</p>*/}
            <TableCell
                className={"cell-sub-table" + (hasPartData ? ' has-data' : '')}
                colSpan={COLUMNS.length}
                style={{paddingLeft: COLUMNS[0].width}}>
                {groupedRow.partStates !== undefined && groupedRow.partCount > 0 &&
                    <Collapse in={open} timeout={getCollapseAnimationTiming(groupedRow.partStates)} unmountOnExit>
                        <GroupedCategorizationReviewSubTable groupedRow={groupedRow}/>
                    </Collapse>
                }
            </TableCell>
        </TableRow>
    </>
})
